import { Injectable } from '@angular/core';
import {
  AdoptionNamcRow,
  AlertRow,
  ConnectivityConditionRow,
  DeploymentByDeviceTypeRow,
  DeploymentNamcRow,
  DeploymentShopRow,
  FilterOption,
  HierarchyCompany,
  KpiCardData,
  NamcEventRow,
  OperationsNamcRow,
  PhaseTrackerRow,
  ScorecardRow,
  ShopMetrics,
  ShopsByPlantRow,
  TimeSavingsRow,
  TrendChartConfig,
  TrendSeries,
  ValueNamcRow,
  WorkOrderRow
} from '../models/reporting.models';

/**
 * Centralised mock data service for all Reporting pages.
 *
 * Structure:
 *  - Static shop-metrics lookup (shopMetricsByNamc) used by Deployment and Health.
 *  - Domain-specific data grouped into readonly getters by page.
 *  - Helper methods (sumShopMetrics, buildShopRows, findShopMetricsKey) previously
 *    duplicated in deployment + health components.
 *
 * Replace the mock return values with real API calls once endpoints are available.
 */
@Injectable({ providedIn: 'root' })
export class ReportingDataService {

  // ── Shop metrics lookup ────────────────────────────────────────────────────
  // Sourced from the EDC Deployment Tracker "Produce Data" report
  // (http://tmmi-wwebp61:5170/producedata), keyed by NAMC then shop suffix.

  readonly shopMetricsByNamc: Readonly<Record<string, Record<string, ShopMetrics>>> = {
    TABC: {
      Stamping: { lines: 4, equipment: 13, events: 7392, tools: 128, toolData: 796, toolDataPdm: 323, pdmSensors: 82, pdmNw: 8, cycleSteps: 0 }
    },
    TMMC: {
      Cambridge_Stamping:  { lines: 4,  equipment: 15,  events: 10332,  tools: 295,  toolData: 1498,  toolDataPdm: 819,  pdmSensors: 212, pdmNw: 10, cycleSteps: 12 },
      wAssembly:           { lines: 22, equipment: 143, events: 277143, tools: 366,  toolData: 1555,  toolDataPdm: 1004, pdmSensors: 460, pdmNw: 27, cycleSteps: 4  },
      Woodstock_Stamping:  { lines: 5,  equipment: 12,  events: 6818,   tools: 199,  toolData: 929,   toolDataPdm: 557,  pdmSensors: 156, pdmNw: 6,  cycleSteps: 3  }
    },
    TMMGT: {
      Stamping: { lines: 0, equipment: 0, events: 0, tools: 0, toolData: 0, toolDataPdm: 0, pdmSensors: 0, pdmNw: 0, cycleSteps: 0 }
    },
    TMMI: {
      eAssy:    { lines: 20, equipment: 257,  events: 282161, tools: 1926,  toolData: 10821, toolDataPdm: 4428, pdmSensors: 1807, pdmNw: 73,  cycleSteps: 641 },
      ePaint:   { lines: 19, equipment: 272,  events: 92470,  tools: 4796,  toolData: 86952, toolDataPdm: 3378, pdmSensors: 882,  pdmNw: 49,  cycleSteps: 131 },
      eWeld:    { lines: 59, equipment: 1219, events: 650001, tools: 11094, toolData: 94278, toolDataPdm: 4228, pdmSensors: 1285, pdmNw: 135, cycleSteps: 45  },
      Plastics: { lines: 30, equipment: 170,  events: 190455, tools: 2097,  toolData: 11868, toolDataPdm: 2389, pdmSensors: 584,  pdmNw: 29,  cycleSteps: 40  },
      RCM:      { lines: 7,  equipment: 66,   events: 33401,  tools: 326,   toolData: 4075,  toolDataPdm: 279,  pdmSensors: 105,  pdmNw: 6,   cycleSteps: 161 },
      Stamping: { lines: 14, equipment: 121,  events: 144306, tools: 1781,  toolData: 7062,  toolDataPdm: 3246, pdmSensors: 874,  pdmNw: 57,  cycleSteps: 209 },
      wAssy:    { lines: 22, equipment: 265,  events: 397837, tools: 2534,  toolData: 23701, toolDataPdm: 4687, pdmSensors: 2095, pdmNw: 92,  cycleSteps: 170 },
      wPaint:   { lines: 18, equipment: 182,  events: 158513, tools: 5837,  toolData: 90498, toolDataPdm: 4240, pdmSensors: 1098, pdmNw: 104, cycleSteps: 954 },
      wWeld:    { lines: 57, equipment: 595,  events: 512667, tools: 5549,  toolData: 38979, toolDataPdm: 2600, pdmSensors: 913,  pdmNw: 106, cycleSteps: 59  }
    },
    TMMK: {
      Assy:     { lines: 29, equipment: 163, events: 328343, tools: 215, toolData: 1757, toolDataPdm: 795, pdmSensors: 230, pdmNw: 17, cycleSteps: 194 },
      Plastics: { lines: 14, equipment: 31,  events: 38951,  tools: 341, toolData: 1365, toolDataPdm: 835, pdmSensors: 178, pdmNw: 22, cycleSteps: 53  },
      Stamping: { lines: 21, equipment: 26,  events: 42357,  tools: 313, toolData: 1554, toolDataPdm: 797, pdmSensors: 220, pdmNw: 8,  cycleSteps: 60  },
      Weld:     { lines: 63, equipment: 27,  events: 29335,  tools: 56,  toolData: 520,  toolDataPdm: 89,  pdmSensors: 39,  pdmNw: 10, cycleSteps: 108 }
    },
    TMMMS: {
      Stamping: { lines: 3, equipment: 11, events: 6691, tools: 203, toolData: 1849, toolDataPdm: 446, pdmSensors: 114, pdmNw: 6, cycleSteps: 2 }
    },
    TMMTN: {
      Casting: { lines: 2, equipment: 22, events: 7236, tools: 207, toolData: 1975, toolDataPdm: 470, pdmSensors: 159, pdmNw: 4, cycleSteps: 0 }
    },
    TMMTX: {
      Assembly: { lines: 22, equipment: 88,  events: 166536, tools: 260,  toolData: 1702,  toolDataPdm: 756,  pdmSensors: 316,  pdmNw: 20, cycleSteps: 38 },
      Paint:    { lines: 0,  equipment: 0,   events: 0,      tools: 0,    toolData: 0,     toolDataPdm: 0,    pdmSensors: 0,    pdmNw: 0,  cycleSteps: 0  },
      Plastics: { lines: 7,  equipment: 23,  events: 7168,   tools: 537,  toolData: 1646,  toolDataPdm: 870,  pdmSensors: 171,  pdmNw: 11, cycleSteps: 0  },
      Sprint:   { lines: 44, equipment: 92,  events: 162455, tools: 160,  toolData: 1486,  toolDataPdm: 0,    pdmSensors: 0,    pdmNw: 0,  cycleSteps: 38 },
      Stamping: { lines: 8,  equipment: 33,  events: 21862,  tools: 698,  toolData: 5403,  toolDataPdm: 1733, pdmSensors: 487,  pdmNw: 20, cycleSteps: 0  },
      Weld:     { lines: 49, equipment: 368, events: 31895,  tools: 2871, toolData: 20738, toolDataPdm: 3769, pdmSensors: 1194, pdmNw: 43, cycleSteps: 0  }
    }
  };

  // ── Helper methods ────────────────────────────────────────────────────────

  sumShopMetrics(namcCode: string): ShopMetrics {
    return Object.values(this.shopMetricsByNamc[namcCode] ?? {}).reduce(
      (totals, m) => ({
        lines:       totals.lines       + m.lines,
        equipment:   totals.equipment   + m.equipment,
        events:      totals.events      + m.events,
        tools:       totals.tools       + m.tools,
        toolData:    totals.toolData    + m.toolData,
        toolDataPdm: totals.toolDataPdm + m.toolDataPdm,
        pdmSensors:  totals.pdmSensors  + m.pdmSensors,
        pdmNw:       totals.pdmNw       + m.pdmNw,
        cycleSteps:  totals.cycleSteps  + m.cycleSteps
      }),
      { lines: 0, equipment: 0, events: 0, tools: 0, toolData: 0, toolDataPdm: 0, pdmSensors: 0, pdmNw: 0, cycleSteps: 0 }
    );
  }

  buildShopRows(namcCode: string): DeploymentShopRow[] {
    return Object.entries(this.shopMetricsByNamc[namcCode] ?? {}).map(([shopKey, metrics]) => ({
      name: `${namcCode}_${shopKey}`,
      ...metrics
    }));
  }

  buildNamcDeploymentRows(namcCodes: string[]): DeploymentNamcRow[] {
    return namcCodes.map(code => ({
      name: code,
      ...this.sumShopMetrics(code),
      shops: this.buildShopRows(code)
    }));
  }

  /**
   * Finds the shopMetricsByNamc key that best matches a real plant/shop pair from
   * the corporate hierarchy. The hierarchy often splits shops by plant
   * (e.g. "PLANT 2 (East)") so we derive an east/west prefix from the plant name
   * and combine it with a normalised shop-type alias to find a match.
   */
  findShopMetricsKey(
    namcShopMetrics: Record<string, ShopMetrics>,
    plantName: string,
    shopName: string
  ): string | null {
    const normalize = (v: string) => (v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const shopTypeAliases: Array<[RegExp, string]> = [
      [/assembl/, 'assy'],
      [/weld/,    'weld'],
      [/paint/,   'paint'],
      [/plastic/, 'plastics'],
      [/stamp/,   'stamping'],
      [/cast/,    'casting'],
      [/rcm/,     'rcm']
    ];

    const normalizedPlant = normalize(plantName);
    const normalizedShop  = normalize(shopName);
    const prefix = normalizedPlant.includes('east') ? 'e'
                 : normalizedPlant.includes('west') ? 'w'
                 : '';
    const aliasMatch = shopTypeAliases.find(([rx]) => rx.test(normalizedShop));

    const candidates = new Set<string>([normalizedShop, `${prefix}${normalizedShop}`]);
    if (aliasMatch) {
      candidates.add(aliasMatch[1]);
      candidates.add(`${prefix}${aliasMatch[1]}`);
      candidates.add(`${normalizedPlant}${aliasMatch[1]}`);
    }

    return Object.keys(namcShopMetrics).find(k => candidates.has(normalize(k))) ?? null;
  }

  mapHierarchyCompanyToNamcRow(company: HierarchyCompany): DeploymentNamcRow {
    const namcCode        = company.company_code?.trim() ?? `Company ${company.company_id}`;
    const namcShopMetrics = this.shopMetricsByNamc[namcCode] ?? {};
    const emptyMetrics: ShopMetrics = {
      lines: 0, equipment: 0, events: 0, tools: 0,
      toolData: 0, toolDataPdm: 0, pdmSensors: 0, pdmNw: 0, cycleSteps: 0
    };

    const shops: DeploymentShopRow[] = (company.plantList ?? []).flatMap(plant =>
      (plant.shopList ?? []).map(shop => {
        const key = this.findShopMetricsKey(namcShopMetrics, plant.plant_name, shop.shop_name);
        return {
          name: `${namcCode}_${shop.shop_name}`,
          ...(key ? namcShopMetrics[key] : emptyMetrics)
        };
      })
    );

    return { name: namcCode, ...this.sumShopMetrics(namcCode), shops };
  }

  // ── Deployment mock data ──────────────────────────────────────────────────

  readonly deploymentKpis: KpiCardData[] = [
    { label: 'NAMCs Enabled',    value: '7 / 8',  delta: '1 remaining',          trend: 'neutral' },
    { label: 'Shops Enabled',    value: '312',     delta: '▲ 18 this quarter',    trend: 'up'      },
    { label: 'Shops Planned',    value: '428',     delta: 'Current scope',         trend: 'neutral' },
    { label: 'In Progress',      value: '76',      delta: 'PH1–PH6',              trend: 'neutral' },
    { label: 'Not Started',      value: '40',      delta: 'Needs scheduling',      trend: 'bad'     },
    { label: 'Enablement Rate',  value: '72.9%',   delta: '▲ 4.2%',              trend: 'up'      }
  ];

  readonly shopsByPlantRows: ShopsByPlantRow[] = [
    { plant: 'TMMI',  totalShops: 6,  completed: 4, inProgress: 2,  notStarted: 0, completionPct: 67 },
    { plant: 'TMMK',  totalShops: 10, completed: 3, inProgress: 5,  notStarted: 2, completionPct: 30 },
    { plant: 'VPM',   totalShops: 6,  completed: 2, inProgress: 3,  notStarted: 1, completionPct: 33 },
    { plant: 'TMMTX', totalShops: 4,  completed: 0, inProgress: 2,  notStarted: 2, completionPct: 0  },
    { plant: 'Total', totalShops: 26, completed: 9, inProgress: 12, notStarted: 5, completionPct: 35 }
  ];

  readonly deploymentByDeviceTypeRows: DeploymentByDeviceTypeRow[] = [
    { deviceType: 'SmartCollector',    deploymentPct: 100, deployed: 2154, remaining: 0   },
    { deviceType: 'Edge Gateway',      deploymentPct: 96,  deployed: 1245, remaining: 54  },
    { deviceType: 'IoT Sensor',        deploymentPct: 91,  deployed: 1206, remaining: 121 },
    { deviceType: 'Configuration Hub', deploymentPct: 87,  deployed: 342,  remaining: 51  },
    { deviceType: 'Other',             deploymentPct: 78,  deployed: 158,  remaining: 37  }
  ];

  readonly phaseTrackerRows: PhaseTrackerRow[] = [
    { plant: 'TMMI', shop: 'Assembly 1',          statuses: ['complete', 'complete', 'progress', 'progress', 'risk',    'planned', 'planned'] },
    { plant: 'TMMI', shop: 'Paint 1',             statuses: ['complete', 'progress', 'progress', 'risk',    'risk',    'planned', 'planned'] },
    { plant: 'TMMI', shop: 'Body Weld 1',         statuses: ['complete', 'complete', 'complete', 'progress', 'risk',   'planned', 'planned'] },
    { plant: 'TMMK', shop: 'Assembly 1 (Line 1)', statuses: ['complete', 'complete', 'progress', 'risk',    'planned', 'planned', 'planned'] },
    { plant: 'TMMK', shop: 'Paint 1 (Line 1)',    statuses: ['complete', 'progress', 'progress', 'risk',    'planned', 'planned', 'planned'] }
  ];

  // ── Health mock data ──────────────────────────────────────────────────────

  readonly healthKpis: KpiCardData[] = [
    { label: 'Devices Connected', value: '4,061', delta: '95.7%',              trend: 'up'  },
    { label: 'Devices Online',    value: '3,982', delta: '93.8%',              trend: 'up'  },
    { label: 'Data Health',       value: '88.95%',delta: 'Below 95% target',   trend: 'bad' },
    { label: 'Data Quality',      value: '70.67%',delta: 'TMMI issue',         trend: 'bad' },
    { label: 'Records Processed', value: '16.6M', delta: 'Today',              trend: 'up'  }
  ];

  readonly connectivityConditions: ConnectivityConditionRow[] = [
    { plant: 'TMMI',  status: 'Offline', lastHeartbeat: '2 days ago', quality: 8.9,  health: 92.3, issue: 'Quality fault surge'  },
    { plant: 'TMMTN', status: 'Stale',   lastHeartbeat: '11 hrs ago', quality: 0.0,  health: 70.0, issue: 'Health faults'        },
    { plant: 'TMMTX', status: 'Online',  lastHeartbeat: '3 min ago',  quality: 82.3, health: 79.7, issue: 'Validation backlog'   }
  ];

  // ── Operations mock data ──────────────────────────────────────────────────

  readonly operationsKpis: KpiCardData[] = [
    { label: 'OA',             value: '87.5%',  delta: '▲ 2.1%',             trend: 'up'  },
    { label: 'OEE',            value: '72.1%',  delta: '▲ 1.6%',             trend: 'up'  },
    { label: 'MTTR',           value: '2.45h',  delta: '▼ 0.3h (improving)', trend: 'up'  },
    { label: 'MTBF',           value: '120h',   delta: '▲ 5h',               trend: 'up'  },
    { label: 'Total Downtime', value: '18.6K',  delta: '▼ 4.7% (improving)', trend: 'up'  },
    { label: '20+ Min Events', value: '412',    delta: '▲ 3.2%',             trend: 'bad' }
  ];

  readonly operationsNamcRows: OperationsNamcRow[] = [
    { namc: 'TMMI',  oa: 88.2, oee: 73.4, mtbf: 124, mttr: 2.31, direction: 'Improving' },
    { namc: 'TABC',  oa: 91.1, oee: 78.2, mtbf: 138, mttr: 1.92, direction: 'Improving' },
    { namc: 'TMMTX', oa: 84.6, oee: 68.9, mtbf: 108, mttr: 2.78, direction: 'Watch'     },
    { namc: 'TMMK',  oa: 87.3, oee: 72.1, mtbf: 120, mttr: 2.36, direction: 'Stable'    },
    { namc: 'TMMMS', oa: 85.5, oee: 70.3, mtbf: 113, mttr: 2.57, direction: 'Stable'    },
    { namc: 'TMMC',  oa: 89.0, oee: 75.6, mtbf: 130, mttr: 2.09, direction: 'Improving' }
  ];

  readonly operationsTrendConfig: Omit<TrendChartConfig, 'yFormat'> & { yFormat: (v: number) => string } = {
    yMin: 60, yMax: 100, yTicks: [100, 90, 80, 70, 60],
    xLabels: ['Jun 16', 'Jun 22', 'Jun 28', 'Jul 10', 'Jul 15'],
    yFormat: (v: number) => `${v}%`,
    series: [
      { name: 'OA',  color: '#2477b5', values: [73.2, 74.8, 76.5, 79.2, 84.0, 85.5, 87.5, 88.3, 89.1, 89.8, 90.0, 87.5] },
      { name: 'OEE', color: '#31a36f', values: [60.5, 62.0, 64.0, 66.3, 68.1, 69.5, 71.2, 72.6, 73.8, 73.2, 72.8, 72.1] }
    ]
  };

  // ── Adoption mock data ────────────────────────────────────────────────────

  readonly adoptionKpis: KpiCardData[] = [
    { label: 'Adoption Rate',         value: '82%',                   delta: '▲ 2.8%',                trend: 'up'      },
    { label: 'Daily Active Users',    value: '1,245',                 delta: '▲ 84',                  trend: 'up'      },
    { label: 'Weekly Active Users',   value: '3,872',                 delta: '▲ 6.4%',               trend: 'up'      },
    { label: 'Report Views',          value: '67,149',                delta: 'Across 27 active reports', trend: 'neutral' },
    { label: 'Most Consumed Report',  value: '2411 Equip Summary',    delta: '35,037 views',          trend: 'neutral' },
    { label: 'Returning Users',       value: '66%',                   delta: '▲ 3.1%',               trend: 'up'      }
  ];

  readonly adoptionNamcRows: AdoptionNamcRow[] = [
    {
      namc: 'TABC', rate: 92, color: '#6f42c1',
      shops: [
        { name: 'Body Weld', rate: 94 },
        { name: 'Stamping',  rate: 93 },
        { name: 'Tool & Die',rate: 89 }
      ]
    },
    {
      namc: 'TMMC', rate: 86, color: '#5c6f82',
      shops: [
        { name: 'North Assembly', rate: 89 },
        { name: 'South Assembly', rate: 87 },
        { name: 'Body Weld',      rate: 84 },
        { name: 'Paint',          rate: 83 }
      ]
    },
    {
      namc: 'TMMI', rate: 79, color: '#6f42c1',
      shops: [
        { name: 'East Assembly', rate: 84 },
        { name: 'West Assembly', rate: 83 },
        { name: 'East Body Weld',rate: 81 },
        { name: 'West Body Weld',rate: 80 },
        { name: 'East Paint',    rate: 77 },
        { name: 'West Paint',    rate: 76 },
        { name: 'Plastics',      rate: 74 },
        { name: 'Stamping',      rate: 72 }
      ]
    },
    {
      namc: 'TMMK', rate: 81, color: '#1b9e77',
      shops: [
        { name: 'Assembly', rate: 85 },
        { name: 'Weld',     rate: 82 },
        { name: 'Paint',    rate: 80 },
        { name: 'Plastics', rate: 78 }
      ]
    },
    {
      namc: 'TMMMS', rate: 75, color: '#d45087',
      shops: [
        { name: 'Body Weld', rate: 77 },
        { name: 'Stamping',  rate: 73 }
      ]
    },
    {
      namc: 'TMMTX', rate: 73, color: '#ef7d00',
      shops: [
        { name: 'Assembly', rate: 78 },
        { name: 'Weld',     rate: 74 },
        { name: 'Paint',    rate: 70 },
        { name: 'Plastics', rate: 69 }
      ]
    }
  ];

  readonly adoptionTrendConfig: TrendChartConfig = {
    yMin: 60, yMax: 100, yTicks: [100, 90, 80, 70, 60],
    xLabels: ['W1', 'W4', 'W8', 'W12'],
    yFormat: (v: number) => `${v}%`,
    series: [
      { name: 'TMMI',  color: '#6f42c1', values: [72, 74, 75, 76, 77, 77, 78, 79, 79, 80, 81, 79] },
      { name: 'TABC',  color: '#0070c0', values: [85, 86, 86, 87, 88, 90, 90, 91, 92, 92, 92, 92] },
      { name: 'TMMTX', color: '#ef7d00', values: [63, 65, 66, 67, 68, 69, 70, 71, 72, 72, 73, 73] },
      { name: 'TMMK',  color: '#1b9e77', values: [74, 75, 76, 77, 78, 79, 80, 80, 81, 81, 81, 81] },
      { name: 'TMMMS', color: '#d45087', values: [68, 69, 70, 71, 72, 73, 73, 74, 75, 75, 75, 75] },
      { name: 'TMMC',  color: '#5c6f82', values: [79, 80, 81, 82, 83, 84, 85, 85, 86, 86, 86, 86] }
    ]
  };

  // ── Value mock data ────────────────────────────────────────────────────────

  readonly valueKpis: KpiCardData[] = [
    { label: 'Events Collected',     value: '42.3M',  delta: 'YTD',                       trend: 'neutral' },
    { label: 'Data Points Captured', value: '512M',   delta: 'YTD',                       trend: 'neutral' },
    { label: 'Manual Entries Avoided',value: '18.7M', delta: 'Estimated',                 trend: 'neutral' },
    { label: 'Time Saved',           value: '125K h', delta: 'YTD estimate',               trend: 'neutral' },
    { label: 'Paper Eliminated',     value: '1.3M',   delta: 'Forms',                     trend: 'neutral' },
    { label: 'Estimated Value',      value: '$12.4M', delta: 'Validated + modeled',        trend: 'up'      }
  ];

  readonly valueNamcRows: ValueNamcRow[] = [
    { namc: 'TMMI',  valuePct: 94, value: '$3.2M' },
    { namc: 'TMMC',  valuePct: 79, value: '$2.7M' },
    { namc: 'TMMK',  valuePct: 71, value: '$2.4M' },
    { namc: 'TMMTX', valuePct: 56, value: '$1.9M' },
    { namc: 'TMMMS', valuePct: 47, value: '$1.6M' },
    { namc: 'TABC',  valuePct: 18, value: '$0.6M' }
  ];

  readonly timeSavingsRows: TimeSavingsRow[] = [
    { rank: 1,  namc: 'TMMI',  shop: 'Stamping 1',   edcWorkOrders: 12415, hoursSaved: 2690, daysSaved: 336.2 },
    { rank: 2,  namc: 'TMMI',  shop: 'Assembly 2',   edcWorkOrders: 10376, hoursSaved: 2248, daysSaved: 281.0 },
    { rank: 3,  namc: 'TMMI',  shop: 'Body Weld 1',  edcWorkOrders: 9812,  hoursSaved: 2126, daysSaved: 265.8 },
    { rank: 4,  namc: 'TMMC',  shop: 'N Assembly',   edcWorkOrders: 8543,  hoursSaved: 1851, daysSaved: 231.4 },
    { rank: 5,  namc: 'TMMK',  shop: 'Assembly 1',   edcWorkOrders: 7219,  hoursSaved: 1564, daysSaved: 195.5 },
    { rank: 6,  namc: 'TMMK',  shop: 'Weld 1',       edcWorkOrders: 6007,  hoursSaved: 1302, daysSaved: 162.7 },
    { rank: 7,  namc: 'TMMTX', shop: 'Assembly',     edcWorkOrders: 3241,  hoursSaved: 702,  daysSaved: 87.7  },
    { rank: 8,  namc: 'TMMMS', shop: 'Body Weld',    edcWorkOrders: 2876,  hoursSaved: 623,  daysSaved: 77.9  },
    { rank: 9,  namc: 'TABC',  shop: 'Stamping',     edcWorkOrders: 1244,  hoursSaved: 270,  daysSaved: 33.7  },
    { rank: 10, namc: 'TMMC',  shop: 'S Assembly',   edcWorkOrders: 1150,  hoursSaved: 249,  daysSaved: 31.1  }
  ];

  // ── Utilization mock data ─────────────────────────────────────────────────

  readonly utilizationKpis: KpiCardData[] = [
    { label: 'Utilization Rate',  value: '76.4%',   delta: '▲ 1.6%',              trend: 'up'      },
    { label: 'EDC Work Orders',   value: '54,983',  delta: 'Planned + unplanned', trend: 'neutral' },
    { label: 'Planned via EDC',   value: '2,793',   delta: '5.1% of EDC WOs',     trend: 'up'      },
    { label: 'Unplanned via EDC', value: '52,190',  delta: '94.9% of EDC WOs',    trend: 'neutral' },
    { label: 'EDC Entry Share',   value: '8.0%',    delta: 'EDC / all exported',  trend: 'up'      },
    { label: 'Time Saved',        value: '11,913 h',delta: '13 min per EDC WO',   trend: 'up'      }
  ];

  readonly workOrderRows: WorkOrderRow[] = [
    { namc: 'TMMI',  shopScope: 'All Shops', plannedEDC: 2776, unplannedEDC: 52168, maximo: 56149,  edcShare: '49.5%', hoursSaved: 11904 },
    { namc: 'TMMTX', shopScope: 'All Shops', plannedEDC: 4,    unplannedEDC: 21,    maximo: 66359,  edcShare: '0.0%',  hoursSaved: 5     },
    { namc: 'TMMK',  shopScope: 'All Shops', plannedEDC: 13,   unplannedEDC: 1,     maximo: 289420, edcShare: '0.0%',  hoursSaved: 3     },
    { namc: 'MTMUS', shopScope: 'All Shops', plannedEDC: 0,    unplannedEDC: 0,     maximo: 98499,  edcShare: '0.0%',  hoursSaved: 0     },
    { namc: 'TMMAL', shopScope: 'All Shops', plannedEDC: 0,    unplannedEDC: 0,     maximo: 60513,  edcShare: '0.0%',  hoursSaved: 0     },
    { namc: 'TMMGT', shopScope: 'All Shops', plannedEDC: 0,    unplannedEDC: 0,     maximo: 36998,  edcShare: '0.0%',  hoursSaved: 0     },
    { namc: 'TMMMS', shopScope: 'All Shops', plannedEDC: 0,    unplannedEDC: 0,     maximo: 30950,  edcShare: '0.0%',  hoursSaved: 0     },
    { namc: 'TABC',  shopScope: 'All Shops', plannedEDC: 0,    unplannedEDC: 0,     maximo: 13451,  edcShare: '0.0%',  hoursSaved: 0     }
  ];

  readonly utilizationTrendConfig: TrendChartConfig = {
    yMin: 50, yMax: 100, yTicks: [100, 90, 80, 70, 60, 50],
    xLabels: ['W1', 'W4', 'W8', 'W12'],
    yFormat: (v: number) => `${v}%`,
    series: [
      { name: 'TMMI',  color: '#2477b5', values: [65, 66, 67, 68, 69, 69, 70, 71, 72, 73, 74, 76] },
      { name: 'TABC',  color: '#f28c28', values: [75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86] },
      { name: 'TMMTX', color: '#7a5bd6', values: [57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68] },
      { name: 'TMMK',  color: '#2d9d78', values: [66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77] },
      { name: 'TMMMS', color: '#d45555', values: [60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71] },
      { name: 'TMMC',  color: '#68768a', values: [70, 71, 72, 73, 74, 75, 76, 77, 78, 79, 80, 81] }
    ]
  };

  // ── Shared filter options ─────────────────────────────────────────────────

  readonly namcOptions: FilterOption[] = [
    { label: 'All NAMCs', value: 'ALL' },
    { label: 'TABC',  value: 'TABC'  },
    { label: 'TMMC',  value: 'TMMC'  },
    { label: 'TMMGT', value: 'TMMGT' },
    { label: 'TMMI',  value: 'TMMI'  },
    { label: 'TMMK',  value: 'TMMK'  },
    { label: 'TMMMS', value: 'TMMMS' },
    { label: 'TMMTN', value: 'TMMTN' },
    { label: 'TMMTX', value: 'TMMTX' },
    { label: 'MTMUS', value: 'MTMUS' },
    { label: 'TMMAL', value: 'TMMAL' }
  ];

  readonly shopTypeOptions: FilterOption[] = [
    { label: 'All Shop Types', value: 'ALL'       },
    { label: 'Assembly',       value: 'Assembly'  },
    { label: 'Body Weld',      value: 'Body Weld' },
    { label: 'Paint',          value: 'Paint'     },
    { label: 'Plastics',       value: 'Plastics'  },
    { label: 'Stamping',       value: 'Stamping'  },
    { label: 'Casting',        value: 'Casting'   },
    { label: 'Tool & Die',     value: 'Tool & Die'},
    { label: 'Battery',        value: 'Battery'   },
    { label: 'Other',          value: 'Other'     }
  ];

  /** Returns true when the shop name contains the selected shop type keyword */
  shopMatchesType(shopName: string, selectedShopType: string): boolean {
    if (selectedShopType === 'ALL') {
      return true;
    }
    const keywords: Record<string, string[]> = {
      Assembly:   ['assy', 'assembl'],
      'Body Weld':['weld'],
      Paint:      ['paint'],
      Plastics:   ['plastic'],
      Stamping:   ['stamp'],
      Casting:    ['cast'],
      'Tool & Die':['tool'],
      Battery:    ['battery'],
      Other:      []
    };
    const kw = keywords[selectedShopType] ?? [];
    const lower = shopName.toLowerCase();
    return kw.some(k => lower.includes(k));
  }

  // ── Snapshot page data ─────────────────────────────────────────────────────

  readonly snapshotKpis: KpiCardData[] = [
    { label: 'Active Devices',     value: '2,847', delta: '+12', trend: 'up' },
    { label: 'Online Rate',        value: '98.4%', delta: '+0.3%', trend: 'up' },
    { label: 'Avg Event Latency',  value: '340ms', delta: '-45ms', trend: 'up' },
    { label: 'Data Ingestion',     value: '1.2M/s', delta: '+8%', trend: 'up' },
    { label: 'Buffer Utilization', value: '62%', delta: '+5%', trend: 'down' },
    { label: 'Alert Count (24h)',  value: '187', delta: '+42', trend: 'down' }
  ];

  readonly currentEventsRows: NamcEventRow[] = [
    { namc: 'TABC', currentEvents: 12, healthAnomalies: 3 },
    { namc: 'TMMC', currentEvents: 18, healthAnomalies: 4 },
    { namc: 'TMMI', currentEvents: 27, healthAnomalies: 7 },
    { namc: 'TMMK', currentEvents: 9, healthAnomalies: 2 },
    { namc: 'TMMTX', currentEvents: 14, healthAnomalies: 5 }
  ];

  readonly scorecardRows: ScorecardRow[] = [
    { namc: 'TABC', status: 'good', oa: 91.8, mttr: 1.6, mtbf: 42.1, uptime: 97.2, downtime: 2.8, deployment: 90, health: 92, utilization: 88, adoption: 84, overallScore: 89 },
    { namc: 'TMMC', status: 'good', oa: 90.2, mttr: 1.9, mtbf: 39.4, uptime: 96.7, downtime: 3.3, deployment: 86, health: 89, utilization: 84, adoption: 80, overallScore: 86 },
    { namc: 'TMMI', status: 'warn', oa: 86.4, mttr: 2.4, mtbf: 32.8, uptime: 94.1, downtime: 5.9, deployment: 73, health: 79, utilization: 76, adoption: 72, overallScore: 75 },
    { namc: 'TMMK', status: 'warn', oa: 84.7, mttr: 2.8, mtbf: 30.2, uptime: 93.6, downtime: 6.4, deployment: 69, health: 74, utilization: 70, adoption: 66, overallScore: 70 },
    { namc: 'TMMTX', status: 'critical', oa: 79.6, mttr: 3.2, mtbf: 26.9, uptime: 91.2, downtime: 8.8, deployment: 62, health: 64, utilization: 60, adoption: 58, overallScore: 61 }
  ];

  readonly recentAlerts: AlertRow[] = [
    { time: '5 min ago', namc: 'TMMI', asset: 'East Weld', alert: 'High vibration detected on spindle motor', status: 'Open' },
    { time: '12 min ago', namc: 'TMMTN', asset: 'Casting', alert: 'Temperature sensor calibration drift', status: 'In Progress' },
    { time: '18 min ago', namc: 'TMMTX', asset: 'Assembly', alert: 'Network packet loss exceeds threshold', status: 'Open' },
    { time: '25 min ago', namc: 'TMMI', asset: 'East Paint', alert: 'Preventive maintenance overdue', status: 'In Progress' },
    { time: '33 min ago', namc: 'TMMK', asset: 'Plastics', alert: 'Humidity level out of spec', status: 'Open' }
  ];
}
